import { Button } from 'ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import './MathField.scss';
import useMathLive from './useMathLive';

const getKeyboard = () => window.mathVirtualKeyboard;

const config = {
  inputMathFloating: true,
  mathFieldIcon: false,
  decimalSeparator: '.',
};

const MathField = ({ text, disabled, onChange, onKeyboardVisible, show = false, onSubmit, onClose, handleFocus = () => {}, t = () => {} }) => {
  const { mathLiveReady } = useMathLive();
  const [withFocus, setWithFocus] = useState(show);
  const mf = useRef();

  function setKeyboardVisible(visible) {
    const keyboard = getKeyboard();
    if (!keyboard) return;
    visible ? keyboard?.show() : keyboard?.hide();

    onKeyboardVisible?.(visible);
  }

  const getValue = () => mf.current?.getValue?.('latex-expanded');

  function handleChange() {
    onChange?.(getValue());
  }

  function selectLayout() {
    const keyboard = getKeyboard();
    if (keyboard) {
      keyboard.layouts = 'default';
    }
  }

  const setFocus = (bool) => {
    if (disabled) return false;
    handleFocus(bool);
    setWithFocus(bool);
    if (bool) {
      selectLayout();
      setKeyboardVisible(true);
    } else {
      setKeyboardVisible(false);
    }
  };

  /* iniciar configuración del teclado */
  useEffect(() => {
    if (mathLiveReady) {
      // mf.current.mathVirtualKeyboardPolicy = 'manual';
      mf.current.mathVirtualKeyboardPolicy = 'sandboxed';
      mf.current.addEventListener('focusin', () => setFocus(true));
      mf.current.addEventListener('blur', () => setTimeout(() => setFocus(false), 200));
      mf.current.addEventListener('input', (event) => {
        if (event.inputType === 'insertLineBreak') {
          setTimeout(() => handleSubmit(), 100);
        }
      });
    }
  }, [mathLiveReady]);

  /* actualizar el tipo de teclado */
  useEffect(() => {
    if (mathLiveReady) {
      selectLayout();
    }
  }, [mathLiveReady]);

  useEffect(() => {
    setWithFocus(show);
    if (show) {
      buttonShow();
    } else {
      buttonHide();
    }
  }, [show, mathLiveReady]);

  /* cambiar el separador por defecto */
  useEffect(() => {
    if (mathLiveReady) {
      window.MathfieldElement.decimalSeparator = config.decimalSeparator;
    }
  }, [config.decimalSeparator, mathLiveReady]);

  /* deshabilitar el componente */
  useEffect(() => {
    if (mathLiveReady) {
      mf.current.disabled = disabled;
      const firstItem = mf.current.shadowRoot.firstChild;
      firstItem.style.pointerEvents = disabled ? 'none' : 'auto';
    }
  }, [mathLiveReady, disabled]);

  /* resetear el valor */
  useEffect(() => {
    if (mathLiveReady) {
      mf.current.value !== text && mf.current.setValue(text);
    }
  }, [text, mathLiveReady]);

  function buttonShow() {
    if (!getKeyboard()) {
      return;
    }
    getKeyboard().show();
    setTimeout(() => {
      mf.current.focus();
    }, 100);
  }

  function buttonHide() {
    if (!getKeyboard()) {
      return;
    }
    getKeyboard().hide();
    mf.current.blur();
  }

  const handleSubmit = () => {
    onSubmit(getValue());
    setTimeout(() => {
      mf.current?.setValue('');
      setFocus(false);
    }, 200);
  };

  const handleClose = () => {
    onClose?.();
    setFocus(false);
  };

  return (
    <div className={`${show ? 'chatbot-mathField-show' : ''} chatbot-mathField chatbot-box-small ${withFocus ? 'chatbot-with-focus' : ''}`}>
      <div className="chatbot-mathField-modal">
        <div className="chatbot-mathField-header">
          <h3 className="chatbot-mathField-title">{t('insert_formula')}</h3>
          <Button className="chatbot-mathField-close" variant="plain" isIconOnly leftIcon="close" ariaLabel={t('common:Close')} onClick={handleClose} />
        </div>
        <div className="chatbot-mathField-content">
          <math-field ref={mf} onInput={handleChange} class="chatbot-mathField-input">
            {text}
          </math-field>
          <button className="chatbot-mathField-submit" onClick={handleSubmit}>
            <span>{t('insert')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MathField;

MathField.propTypes = {
  text: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyboardVisible: PropTypes.func,
  handleFocus: PropTypes.func,
  show: PropTypes.bool,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  t: PropTypes.func,
};
